import React, { useEffect, useState } from "react";
import { Box, Button, LinearProgress, TextField, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  ALL_EXAMS,
  WINDOW_DAYS,
  dominantExamType,
  filterByExamType,
  toTrendPoints
} from "../../utils/performance-trend";
import {
  READINESS_DEFAULTS,
  VERDICTS,
  VERDICT_COPY,
  readinessBasis,
  readinessVerdict
} from "../../utils/readiness";
import "./readiness-card.scss";

// Basis-forward readiness card (readiness design §3b/§3c): evidence first,
// verdict second, abstain below the floor. Never a naked checkmark.
//
// Slice 3c (perf-depth design §4): when the student has set a goal for the
// selected exam (readinessGoals[exam]), the TARGET BAND anchors to it and is
// labeled "your goal"; otherwise the house default, labeled "house target".
// The abstain floor and every basis line are untouched — a vanity goal can
// change the bar, never the evidence. Goals are per exam type, so the mixed
// "all exams" view always uses the house target.
const GOAL_MIN = 50;
const GOAL_MAX = 95;

const ReadinessCard = ({
  quizzes,
  now = Date.now(),
  examChoice = null,
  readinessGoals = null,
  onSaveGoal = null
}) => {
  const history = useHistory();
  // The editor is pinned to the exam it was OPENED for: if the shared exam
  // selection changes underneath it, it CLOSES (state cleared, not hidden —
  // switching back must not resurrect a stale draft; re-gate note) rather
  // than silently saving one exam's draft onto another.
  const [editorExam, setEditorExam] = useState(null);
  const [goalDraft, setGoalDraft] = useState("");
  const allPoints = toTrendPoints(quizzes);
  const examSelected = examChoice ?? (dominantExamType(allPoints) || ALL_EXAMS);

  useEffect(() => {
    if (editorExam !== null && editorExam !== examSelected) {
      setEditorExam(null);
      setGoalDraft("");
    }
  }, [editorExam, examSelected]);

  if (!Array.isArray(quizzes) || allPoints.length === 0) {
    return null;
  }
  const points = filterByExamType(allPoints, examSelected);
  const storedGoal =
    examSelected !== ALL_EXAMS && readinessGoals ? readinessGoals[examSelected] : undefined;
  const goalPct =
    Number.isFinite(storedGoal) && storedGoal >= GOAL_MIN && storedGoal <= GOAL_MAX
      ? storedGoal
      : null;
  const readinessOpts =
    goalPct !== null ? { ...READINESS_DEFAULTS, targetPct: goalPct } : READINESS_DEFAULTS;
  const basis = readinessBasis(points, now, readinessOpts);
  const verdict = readinessVerdict(basis, readinessOpts);
  const { current, delta, coverage, weak, stale, excludedQuizzes } = basis;

  // Whole numbers only — parseInt would silently truncate "90.9" to 90, so a
  // non-integer draft disables Save instead of saving something else.
  const draftIsInteger = /^\d+$/.test(goalDraft.trim());
  const draftValue = draftIsInteger ? Number.parseInt(goalDraft.trim(), 10) : NaN;
  const draftValid = draftIsInteger && draftValue >= GOAL_MIN && draftValue <= GOAL_MAX;
  const canEditGoal = typeof onSaveGoal === "function" && examSelected !== ALL_EXAMS;
  const editingGoal = editorExam !== null && editorExam === examSelected;

  const weakAreaDisciplines = [
    ...weak.map((entry) => entry.discipline),
    ...stale.map((entry) => entry.discipline)
  ];
  const thinSpots = [
    ...weak.map((entry) => `${entry.discipline} (${entry.accuracy}% recent)`),
    ...stale.map((entry) => `${entry.discipline} (last seen ${moment(entry.lastSeen).from(now)})`)
  ];

  return (
    <Box component="div" className="readiness-card" data-testid="readiness-card">
      <Box component="div" className="readiness-header">
        <Typography component="h6" variant="h6" className="readiness-title">
          Readiness
          {examSelected !== ALL_EXAMS ? ` — ${examSelected}` : " — all exams (mixed)"}
        </Typography>
        <Box component="span" className="readiness-disclaimer">
          indicator, not a prediction
        </Box>
      </Box>

      <Box component="dl" className="readiness-basis">
        <Box component="div" className="basis-row">
          <dt>Basis</dt>
          <dd data-testid="readiness-basis-line">
            last {WINDOW_DAYS} days · {current.questions} questions · {current.quizzes}{" "}
            {current.quizzes === 1 ? "quiz" : "quizzes"}
            {excludedQuizzes > 0 &&
              ` · ${excludedQuizzes} tutor-assisted ${excludedQuizzes === 1 ? "quiz" : "quizzes"} excluded`}
          </dd>
        </Box>
        <Box component="div" className="basis-row">
          <dt>Recent accuracy</dt>
          <dd>
            {current.accuracy !== null ? (
              <>
                <strong>{current.accuracy}%</strong>
                <Box component="span" className="basis-target" data-testid="readiness-target">
                  {" "}
                  · target ≥{readinessOpts.targetPct}% ({goalPct !== null ? "your goal" : "house target"})
                </Box>
                {canEditGoal && !editingGoal && (
                  <Button
                    size="small"
                    className="goal-edit"
                    data-testid="goal-edit"
                    onClick={() => {
                      setGoalDraft(goalPct !== null ? String(goalPct) : "");
                      setEditorExam(examSelected);
                    }}
                  >
                    {goalPct !== null ? "Edit goal" : "Set goal"}
                  </Button>
                )}
                {/* D10: goal-setting requires a single exam. In the mixed
                    "all exams" view the #540 hook is otherwise undiscoverable —
                    point the student at the filter. */}
                {typeof onSaveGoal === "function" && examSelected === ALL_EXAMS && (
                  <Box component="span" className="goal-hint" data-testid="goal-hint">
                    {" "}
                    Filter to a single exam above to set a goal.
                  </Box>
                )}
                {canEditGoal && editingGoal && (
                  <Box component="div" className="goal-editor" data-testid="goal-editor">
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{
                        min: GOAL_MIN,
                        max: GOAL_MAX,
                        "aria-label": `Goal for ${examSelected} (${GOAL_MIN}–${GOAL_MAX})`,
                        "data-testid": "goal-input"
                      }}
                      value={goalDraft}
                      onChange={(event) => setGoalDraft(event.target.value)}
                    />
                    <Button
                      size="small"
                      color="primary"
                      data-testid="goal-save"
                      disabled={!draftValid}
                      onClick={() => {
                        onSaveGoal(editorExam, draftValue);
                        setEditorExam(null);
                      }}
                    >
                      Save
                    </Button>
                    {goalPct !== null && (
                      <Button
                        size="small"
                        data-testid="goal-clear"
                        onClick={() => {
                          onSaveGoal(editorExam, null);
                          setEditorExam(null);
                        }}
                      >
                        Use house target
                      </Button>
                    )}
                    <Button size="small" data-testid="goal-cancel" onClick={() => setEditorExam(null)}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              "no questions in the window"
            )}
          </dd>
        </Box>
        <Box component="div" className="basis-row">
          <dt>Trend</dt>
          <dd>
            {delta !== null &&
              `${delta > 0 ? "+" : ""}${delta} pts vs the prior ${WINDOW_DAYS} days`}
            {delta === null && "no prior-period data yet"}
          </dd>
        </Box>
        {coverage.lifetime > 0 && (
          <Box component="div" className="basis-row">
            <dt>Coverage</dt>
            <dd>
              {coverage.recent}/{coverage.lifetime} of your disciplines practiced recently
            </dd>
          </Box>
        )}
      </Box>

      {/* D11: at-a-glance accuracy-vs-target meter (design §3b). The reader no
          longer does the arithmetic — the bar fills to recent accuracy, is
          colored by the verdict band (house trio), and a tick marks the
          target. Only shown when there IS a recent-accuracy read. */}
      {current.accuracy !== null && (
        <Box component="div" className="readiness-meter" data-testid="readiness-meter">
          <Box component="div" className="readiness-meter-track">
            <LinearProgress
              variant="determinate"
              value={Math.min(Math.max(current.accuracy, 0), 100)}
              className={`readiness-meter-bar meter-${verdict}`}
            />
            <Box
              component="div"
              className="readiness-meter-tick"
              data-testid="readiness-meter-tick"
              style={{ left: `${readinessOpts.targetPct}%` }}
              aria-hidden="true"
            />
          </Box>
          <Box component="div" className="readiness-meter-scale">
            <Box component="span">Accuracy {current.accuracy}%</Box>
            <Box component="span">Target {readinessOpts.targetPct}%</Box>
          </Box>
        </Box>
      )}

      <Box
        component="div"
        className={`readiness-verdict verdict-${verdict}`}
        data-testid="readiness-verdict"
      >
        {VERDICT_COPY[verdict]}
      </Box>

      {verdict !== VERDICTS.ABSTAIN && thinSpots.length > 0 && (
        <Box component="div" className="readiness-thin-spots">
          <Box component="span" className="thin-spots-label">Thin spots:</Box>{" "}
          {thinSpots.join(", ")}
        </Box>
      )}

      <Button
        variant="outlined"
        color="primary"
        className="weak-areas-cta"
        onClick={() =>
          history.push({
            pathname: "/create-quiz",
            // Router location state: ephemeral per-navigation payload — no
            // localStorage, nothing persists past this history entry. The
            // create-quiz page validates every name against the categories
            // endpoint before preselecting (weak-area-prefill.js).
            state:
              weakAreaDisciplines.length > 0
                ? { weakAreaDisciplines }
                : undefined
          })
        }
      >
        Quiz my weak areas
      </Button>
    </Box>
  );
};

export default ReadinessCard;
